import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { StockService } from 'src/app/core/services/stock.service';
import { UploadService } from 'src/app/core/services/upload.service';
import { environment } from 'src/environments/environment';
import { LoginService } from 'src/app/auth/login.service';
interface FileManagerResponse {
  fileId: number;
  fileName: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  fileExt?: string;
  description?: string;
  owner?: number;
  createDate?: string;
  createUser?: string;
  lastUpdDate?: string;
  lastUpdUser?: string;
  resizePath?: string;
  scope?: string;
  sockId?: number;
}

interface UploadApiResponse {
  data?: FileManagerResponse;
  message?: string;
  code?: string;
}

interface SelectedFile {
  file: File;
  preview: string;
  name: string;
  size: number;
  type: string;
  uploading?: boolean;
  uploadError?: boolean;
  existingImagePath?: string;
}

interface PreviewItem {
  preview: string;
  name: string;
  type: string;
  removable: boolean;
  source: 'existing' | 'new';
  imageId: string;
  removeIndex: number | null;
}

@Component({
  selector: 'app-my-stock',
  templateUrl: './my-stock.component.html',
  styleUrls: ['./my-stock.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyStockComponent implements OnDestroy {
  @Output() stockUpdated = new EventEmitter<any>();
  @Output() stockDeleted = new EventEmitter<number>();

  @ViewChildren('fileInput') fileInputRefs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('itemCard') itemCardRefs!: QueryList<ElementRef<HTMLElement>>;

  isVisible = false;
  loadingDetail = false;
  uploading = false;
  deleting = false;
  errorMessage = '';
  highlightedItemId: number | null = null;
  openFrom: 'userstock' | 'medialist' = 'medialist';

  planForm: FormGroup;

  existingImagesByItem: string[][] = [];
  selectedFilesByItem: SelectedFile[][] = [];
  activePreviewIndexByItem: number[] = [];
  deletedItemIds: number[] = [];

  constructor(

    private loginService: LoginService,
    private fb: FormBuilder,
    private stockService: StockService,
    private uploadService: UploadService,
    private cdr: ChangeDetectorRef
  ) {
    this.planForm = this.fb.group({
      id: [null],
      title: [' ', Validators.required],
      warehouseName: ['', Validators.required],
      description: [''],
      items: this.fb.array([]),
    });

    this.resetForm();
  }

  get isAdminRole(): boolean {
  return this.loginService.getUserCode() === 'admin';
}

  get items(): FormArray {
    return this.planForm.get('items') as FormArray;
  }

  get itemControls(): FormGroup[] {
    return this.items.controls as FormGroup[];
  }

  ngOnDestroy(): void {
    document.body.classList.remove('overflow-hidden');
  }

  open( stockId?: number, source: 'userstock' | 'medialist' = 'medialist', itemId?: number | null): void {
    this.isVisible = true;
    this.loadingDetail = false;
    this.uploading = false;
    this.deleting = false;
    this.errorMessage = '';
    this.openFrom = source;
    this.highlightedItemId = itemId || null;

    document.body.classList.add('overflow-hidden');

    if (stockId) {
      this.loadData(stockId);
    } else {
      this.resetForm();
    }

    this.cdr.markForCheck();
  }

  close(): void {
    this.isVisible = false;
    this.loadingDetail = false;
    this.uploading = false;
    this.deleting = false;
    this.errorMessage = '';
    document.body.classList.remove('overflow-hidden');
    this.cdr.markForCheck();
  }

  createItemForm(item?: any): FormGroup {
    return this.fb.group({
      id: [item?.id || null],
      name: [item?.name || '', Validators.required],
      type: [item?.type || ''],
      unit: [item?.unit || 'sản phẩm'],
      quantity: [item?.quantity ?? null, [Validators.required, Validators.min(1)]],
      soldQty: [item?.soldQty ?? null, [Validators.min(0)]],
      retailPrice: [item?.retailPrice ?? null, [Validators.required, Validators.min(0)]],
      wholesaleTotal: [item?.wholesaleTotal ?? null, [Validators.required, Validators.min(0)]],
      isActive: [item?.isActive ?? false],
      note: [item?.note || ''],
      userCodeContract: [item?.userCodeContract || ''],
    });
  }

  addItem(): void {
    this.items.push(this.createItemForm());
    this.existingImagesByItem.push([]);
    this.selectedFilesByItem.push([]);
    this.activePreviewIndexByItem.push(0);
    this.cdr.markForCheck();
  }

  removeItem(index: number): void {
    const itemId = this.items.at(index)?.get('id')?.value;
    if (itemId) {
      this.deletedItemIds.push(itemId);
    }
    if (this.items.length === 1) {
      this.items.at(0).reset({
        id: null,
        name: '',
        type: '',
        unit: 'sản phẩm',
        quantity: null,
        soldQty: null,
        retailPrice: null,
        wholesaleTotal: null,
        isActive: false,
        note: '',
      });
      this.existingImagesByItem[0] = [];
      this.selectedFilesByItem[0] = [];
      this.activePreviewIndexByItem[0] = 0;
      this.resetFileInput(0);
    } else {
      this.items.removeAt(index);
      this.existingImagesByItem.splice(index, 1);
      this.selectedFilesByItem.splice(index, 1);
      this.activePreviewIndexByItem.splice(index, 1);
      this.resetAllFileInputs();
    }
    this.cdr.markForCheck();
  }

  private loadData(stockId: number): void {
    this.loadingDetail = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    this.stockService.getMyDetail(stockId, null).subscribe({
      next: (res: any) => {
        const data = res?.data || {};
        const productItems = Array.isArray(data?.items) ? data.items : [];

        this.planForm.patchValue({
          id: data?.id ?? stockId,
          title: data?.title ?? '',
          warehouseName: data?.warehouseName ?? '',
          description: data?.description ?? '',
        });

        this.items.clear();
        this.existingImagesByItem = [];
        this.selectedFilesByItem = [];
        this.activePreviewIndexByItem = [];

        if (productItems.length > 0) {
          productItems.forEach((item: any) => {
            this.items.push(
              this.createItemForm({
                id: item?.id ?? null,
                name: item?.name ?? '',
                type: item?.type ?? '',
                unit: item?.unit ?? 'sản phẩm',
                quantity: item?.quantity ?? null,
                soldQty: item?.soldQty ?? null,
                retailPrice: item?.retailPrice ?? null,
                wholesaleTotal: item?.wholesaleTotal ?? null,
                isActive: item?.isActive ?? false,
                note: item?.note ?? '',
                userCodeContract: item?.userCodeContract ?? '',
              })
            );

            const existingImagesRaw: string[] = Array.isArray(item?.existingImages)
              ? item.existingImages
              : [];

            const normalizedUrls = existingImagesRaw
              .filter((path: string) => !!path)
              .map((path: string) => this.buildImageUrl(path));

            this.existingImagesByItem.push(normalizedUrls);
            this.selectedFilesByItem.push([]);
            this.activePreviewIndexByItem.push(0);
          });
        } else {
          this.items.push(this.createItemForm());
          this.existingImagesByItem.push([]);
          this.selectedFilesByItem.push([]);
          this.activePreviewIndexByItem.push(0);
        }

        this.loadingDetail = false;
        this.cdr.markForCheck();

        // Scroll to highlighted item after data is loaded
        if (this.highlightedItemId !== null) {
          setTimeout(() => this.scrollToHighlightedItem(), 100);
        }
      },
      error: (err) => {
        console.error('Không thể tải dữ liệu kho:', err);
        this.errorMessage = 'Không thể tải dữ liệu kho. Vui lòng thử lại.';
        this.loadingDetail = false;
        this.cdr.markForCheck();
      },
    });
  }

  resetForm(): void {
    this.planForm.reset({
      id: null,
      title: 'KẾ HOẠCH KINH DOANH',
      warehouseName: 'TỔNG KHO MỸ PHẨM NỘI ĐỊA',
      description: '',
    });

    this.items.clear();
    this.items.push(this.createItemForm());

    this.existingImagesByItem = [[]];
    this.selectedFilesByItem = [[]];
    this.activePreviewIndexByItem = [0];

    this.resetAllFileInputs();
    this.cdr.markForCheck();
  }

  onFileSelected(event: Event, itemIndex: number): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    Array.from(input.files).forEach((file) => {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        console.warn(`File "${file.name}" không hợp lệ cho item.`);
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const currentPreviewCount = this.getPreviewItems(itemIndex).length;
        const isFirstNewFile = (this.selectedFilesByItem[itemIndex] || []).length === 0;

        const newFile: SelectedFile = {
          file,
          preview: reader.result as string,
          name: file.name,
          size: file.size,
          type: file.type,
          uploading: true,
          uploadError: false,
        };

        this.selectedFilesByItem[itemIndex] = [
          ...(this.selectedFilesByItem[itemIndex] || []),
          newFile,
        ];

        const insertedIndex = this.selectedFilesByItem[itemIndex].length - 1;

        if (isFirstNewFile) {
          this.activePreviewIndexByItem[itemIndex] = currentPreviewCount;
        }

        this.cdr.markForCheck();

        this.uploadService.uploadFile(file).subscribe({
          next: (res: UploadApiResponse) => {
            const uploadedFile = res?.data;
            const uploadedPath = uploadedFile?.filePath || uploadedFile?.resizePath || '';

            const currentFile = this.selectedFilesByItem[itemIndex]?.[insertedIndex];
            if (currentFile) {
              currentFile.uploading = false;
              currentFile.uploadError = false;
              currentFile.existingImagePath = uploadedPath;
            }

            if (uploadedPath) {
              const imageUrl = this.buildImageUrl(uploadedPath);
              this.existingImagesByItem[itemIndex] = [
                ...(this.existingImagesByItem[itemIndex] || []),
                imageUrl,
              ];
            }

            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error(`Upload file "${file.name}" thất bại:`, err);

            const currentFile = this.selectedFilesByItem[itemIndex]?.[insertedIndex];
            if (currentFile) {
              currentFile.uploading = false;
              currentFile.uploadError = true;
            }

            this.cdr.markForCheck();
          },
        });
      };

      reader.readAsDataURL(file);
    });

    this.resetFileInput(itemIndex);
  }

  removeFile(itemIndex: number, previewIndex: number): void {
    const previewItems = this.getPreviewItems(itemIndex);
    const target = previewItems[previewIndex];

    if (!target) return;

    if (target.source === 'existing') {
      this.existingImagesByItem[itemIndex] = (this.existingImagesByItem[itemIndex] || []).filter(
        (url) => url !== target.imageId
      );

      this.selectedFilesByItem[itemIndex] = (this.selectedFilesByItem[itemIndex] || []).filter(
        (file) => this.buildImageUrl(file.existingImagePath || '') !== target.imageId
      );
    } else {
      const fileIndex = target.removeIndex;
      const currentFiles = this.selectedFilesByItem[itemIndex] || [];

      if (fileIndex == null || fileIndex < 0 || fileIndex >= currentFiles.length) return;

      const removedFile = currentFiles[fileIndex];
      const removedUrl = this.buildImageUrl(removedFile.existingImagePath || '');

      this.selectedFilesByItem[itemIndex] = currentFiles.filter((_, i) => i !== fileIndex);

      if (removedUrl) {
        this.existingImagesByItem[itemIndex] = (this.existingImagesByItem[itemIndex] || []).filter(
          (url) => url !== removedUrl
        );
      }
    }

    const nextPreviewItems = this.getPreviewItems(itemIndex);

    if (!nextPreviewItems.length) {
      this.activePreviewIndexByItem[itemIndex] = 0;
    } else if (this.activePreviewIndexByItem[itemIndex] >= nextPreviewItems.length) {
      this.activePreviewIndexByItem[itemIndex] = nextPreviewItems.length - 1;
    }

    this.cdr.markForCheck();
  }

  setActivePreview(itemIndex: number, previewIndex: number): void {
    const previewItems = this.getPreviewItems(itemIndex);
    if (previewIndex < 0 || previewIndex >= previewItems.length) return;

    this.activePreviewIndexByItem[itemIndex] = previewIndex;
    this.cdr.markForCheck();
  }

  onSwipeLeft(itemIndex: number): void {
    const currentIndex = this.activePreviewIndexByItem[itemIndex] || 0;
    const previewItems = this.getPreviewItems(itemIndex);
    const nextIndex = (currentIndex + 1) % previewItems.length;
    this.setActivePreview(itemIndex, nextIndex);
  }

  onSwipeRight(itemIndex: number): void {
    const currentIndex = this.activePreviewIndexByItem[itemIndex] || 0;
    const previewItems = this.getPreviewItems(itemIndex);
    const prevIndex = currentIndex === 0 ? previewItems.length - 1 : currentIndex - 1;
    this.setActivePreview(itemIndex, prevIndex);
  }

  getPreviewItems(itemIndex: number): PreviewItem[] {
    const existing: PreviewItem[] = (this.existingImagesByItem[itemIndex] || []).map(
      (url, index) => ({
        preview: url,
        name: `Ảnh ${index + 1}`,
        type: this.detectMediaType(url),
        removable: true,
        source: 'existing',
        imageId: url,
        removeIndex: index,
      })
    );

    const uploadingFiles = (this.selectedFilesByItem[itemIndex] || [])
      .filter((file) => !file.existingImagePath)
      .map((file, index) => ({
        preview: file.preview,
        name: file.name,
        type: file.type,
        removable: true,
        source: 'new' as const,
        imageId: file.preview,
        removeIndex: index,
      }));

    return [...existing, ...uploadingFiles];
  }

  getActivePreview(itemIndex: number): PreviewItem | null {
    const previewItems = this.getPreviewItems(itemIndex);
    if (!previewItems.length) return null;

    const activeIndex = this.activePreviewIndexByItem[itemIndex] || 0;
    if (activeIndex < 0 || activeIndex >= previewItems.length) {
      return previewItems[0];
    }

    return previewItems[activeIndex];
  }

  getItemRevenue(item: AbstractControl): number {
    const quantity = Number(item.get('quantity')?.value || 1);
    const retailPrice = Number(item.get('retailPrice')?.value || 1);
    return quantity * retailPrice;
  }

  getItemWholesale(item: AbstractControl): number {
    return this.getItemRevenue(item) * 0.9;
  }

  getItemProfit(item: AbstractControl): number {
    return this.getItemRevenue(item) - this.getItemWholesale(item);
  }

  get totalRevenue(): number {
    return this.items.controls.reduce((sum, item) => sum + this.getItemRevenue(item), 1);
  }

  get totalWholesale(): number {
    return this.items.controls.reduce((sum, item) => sum + this.getItemWholesale(item), 1);
  }

  get totalProfit(): number {
    return this.totalRevenue - this.totalWholesale;
  }

  hasUploadingFile(): boolean {
    return this.selectedFilesByItem.some((itemFiles) =>
      (itemFiles || []).some((file) => file.uploading)
    );
  }

  hasUploadError(): boolean {
    return this.selectedFilesByItem.some((itemFiles) =>
      (itemFiles || []).some((file) => file.uploadError)
    );
  }

  canSubmit(): boolean {
    return (
      this.planForm.valid &&
      !this.uploading &&
      !this.loadingDetail &&
      !this.hasUploadingFile() &&
      !this.hasUploadError()
    );
  }

  submit(): void {
    if (!this.canSubmit()) {
      this.planForm.markAllAsTouched();

      if (this.hasUploadingFile()) {
        this.errorMessage = 'Vui lòng chờ tải file lên hoàn tất.';
      } else if (this.hasUploadError()) {
        this.errorMessage = 'Có file tải lên thất bại. Vui lòng xoá file lỗi hoặc tải lại.';
      }

      this.cdr.markForCheck();
      return;
    }

    this.uploading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    const itemsPayload = this.itemControls.map((control, index) => ({
      ...control.getRawValue(),
      existingImages: (this.existingImagesByItem[index] || []).map((url) =>
        this.toRelativeUploadPath(url)
      ),
      revenue: this.getItemRevenue(control),
      profit: this.getItemProfit(control),
    }));

    const payload = {
      ...this.planForm.getRawValue(),
      items: itemsPayload,
      deletedItemIds: this.deletedItemIds,
      totalRevenue: this.totalRevenue,
      totalWholesale: this.totalWholesale,
      totalProfit: this.totalProfit,
    };

    this.stockService.editStock(payload).subscribe({
      next: (response) => {
        this.stockUpdated.emit(response);
        this.uploading = false;
        this.close();
      },
      error: (err) => {
        console.error('Không thể cập nhật kế hoạch kho:', err);
        this.errorMessage = 'Không thể lưu dữ liệu. Vui lòng thử lại.';
        this.uploading = false;
        this.cdr.markForCheck();
      },
    });
  }

  deleteStock(): void {
    const id = this.planForm.get('id')?.value;
    if (!id || this.uploading || this.loadingDetail || this.deleting) return;
    if (!confirm('Bạn có chắc muốn xóa dữ liệu này?')) return;

    this.deleting = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    this.stockService.deleteStock(id).subscribe({
      next: () => {
        this.stockDeleted.emit(id);
        this.deleting = false;
        this.close();
      },
      error: (err) => {
        console.error('Không thể xóa dữ liệu:', err);
        this.errorMessage = 'Không thể xóa dữ liệu. Vui lòng thử lại.';
        this.deleting = false;
        this.cdr.markForCheck();
      },
    });
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('vi-VN').format(value || 0);
  }

  getFormattedItemValue(
    itemIndex: number,
    field: 'quantity' | 'soldQty' | 'retailPrice' | 'wholesaleTotal'
  ): string {
    const control = this.items.at(itemIndex)?.get(field);
    const value = control?.value;
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return this.formatCurrency(Number(value));
  }

  onFormattedNumberInput(
    event: Event,
    itemIndex: number,
    field: 'quantity' | 'soldQty' | 'retailPrice' | 'wholesaleTotal'
  ): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    const digits = input.value.replace(/[^0-9]/g, '').slice(0, 12);
    const numericValue = digits ? Number(digits) : null;
    const control = this.items.at(itemIndex)?.get(field);

    control?.setValue(numericValue, { emitEvent: false });
    control?.markAsDirty();
    control?.markAsTouched();

    input.value = numericValue === null ? '' : this.formatCurrency(numericValue);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private detectMediaType(url: string): string {
    const lower = (url || '').toLowerCase();
    if (/\.(mp4|webm|ogg|mov|m4v)(\?|$)/.test(lower)) {
      return 'video/mp4';
    }
    return 'image/jpeg';
  }

  private resetFileInput(itemIndex: number): void {
    const fileInput = this.fileInputRefs?.toArray()?.[itemIndex];
    if (fileInput?.nativeElement) {
      fileInput.nativeElement.value = '';
    }
  }

  private resetAllFileInputs(): void {
    if (!this.fileInputRefs) return;
    this.fileInputRefs.forEach((ref) => {
      if (ref?.nativeElement) {
        ref.nativeElement.value = '';
      }
    });
  }

  private safeJoinUrl(base: string, path: string): string {
    if (!base) return path || '';
    if (!path) return base;
    if (/^https?:\/\//i.test(path)) return path;
    return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }

  private normalizeUploadPath(rawPath: string): string {
    if (!rawPath) return '';
    let normalized = rawPath.replace(/\\/g, '/');
    const marker = '/uploads/';
    const markerIndex = normalized.lastIndexOf(marker);
    if (markerIndex !== -1) {
      normalized = normalized.substring(markerIndex + 1);
    }
    return normalized.replace(/^\/+/, '');
  }

  private buildImageUrl(path: string): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return this.safeJoinUrl(environment.apiUrl, this.normalizeUploadPath(path));
  }

  private toRelativeUploadPath(url: string): string {
    if (!url) return '';

    const normalized = url.replace(/\\/g, '/');
    const marker = '/uploads/';
    const markerIndex = normalized.lastIndexOf(marker);

    if (markerIndex !== -1) {
      return normalized.substring(markerIndex + 1);
    }

    return normalized.replace(/^\/+/, '');
  }

  private scrollToHighlightedItem(): void {
    if (this.highlightedItemId === null || !this.itemCardRefs) return;

    const itemCards = this.itemCardRefs.toArray();
    const highlightedIndex = this.items.controls.findIndex(
      (ctrl) => ctrl.get('id')?.value === this.highlightedItemId
    );

    if (highlightedIndex >= 0 && itemCards[highlightedIndex]) {
      const element = itemCards[highlightedIndex].nativeElement;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.cdr.markForCheck();
    }
  }
}
